import { inject, ref } from 'vue';
import { defineStore } from 'pinia';
import baseAxios from 'axios';

import { AXIOS } from '../const';

import * as api from '../router/endpoints';

export const useAutoformStore = defineStore('autoform', () => {
  const axios = inject(AXIOS) || baseAxios;

  const autoformList = ref([]);
  const autoform = ref({});
  const variableList = ref([]);
  const variableTypeList = ref({
    text: 'text',
    double: 'double',
    boolean: 'boolean',
    identificator: 'identificator',
    date: 'date',
    line: 'line',
    point: 'point',
    polygon: 'polygon',
    geometry: 'geometry',
    document: 'document',
    array: 'array',
    query: 'query',
    map_layers: 'map_layers',
  });
  const outputVariableList = ref([]);
  const operationList = ref([]);
  const selectedCustomVariableIndex = ref(null);
  const selectedInstructionStep = ref(null);
  const name = ref(null);
  const instruction_steps = ref([]);
  const custom_variables = ref([]);
  const output_entities = ref([]);
  const selectedOutputIndex = ref(null);
  const newUuid = ref(null);
  const autoCatalogs = ref([]);
  const selectedAutoCatalogIndex = ref(null);
  const mapLayers = ref([]);
  const selectedMapLayersIndex = ref(null);

  const getAutoformListRequest = ref({
    isLoading: false,
    isFinished: false,
    error: null
  });

  const getVariableListRequest = ref({
    isLoading: false,
    isFinished: false,
    error: null
  });

  const getOutputVariableListRequest = ref({
    isLoading: false,
    isFinished: false,
    error: null
  });

  const getOperationListRequest = ref({
    isLoading: false,
    isFinished: false,
    error: null
  });

  const createAutoformRequest = ref({
    isLoading: false,
    isFinished: false,
    error: null
  });

  const deleteAutoformRequest = ref({
    isLoading: false,
    isFinished: false,
    error: null
  });

  const updateAutoformRequest = ref({
    isLoading: false,
    isFinished: false,
    error: null
  });

  const generateUuidRequest = ref({
    isLoading: false,
    isFinished: false,
    error: null
  });

  const getAutoformRequest = ref({
    isLoading: false,
    isFinished: false,
    error: null
  });

  function getAutoformList(projectId, bpId, nodeId) {
    getAutoformListRequest.value = {};
    const req = getAutoformListRequest.value;
    req.isLoading = true;
    return axios
      .get(api.autoforms({ projectId, bpId, nodeId }))
      .then((resp) => {
        autoformList.value = resp.data;
      })
      .catch((e) => {
        req.error = e;
        throw e;
      })
      .finally(() => {
        req.isFinished = true;
        req.isLoading = false;
      });
  }

  function getVariableList(projectId, bpId, nodeId) {
    getVariableListRequest.value = {};
    const req = getVariableListRequest.value;
    req.isLoading = true;
    return axios
      .get(api.autoformVariables({ projectId, bpId, nodeId }))
      .then((resp) => {
        variableList.value = resp.data;
      })
      .catch((e) => {
        req.error = e;
        throw e;
      })
      .finally(() => {
        req.isFinished = true;
        req.isLoading = false;
      });
  }

  function getOutputVariableList(projectId, bpId, nodeId) {
    getOutputVariableListRequest.value = {};
    const req = getOutputVariableListRequest.value;
    req.isLoading = true;

    return axios
      .get(api.autoformOutputVariables({ projectId, bpId, nodeId }))
      .then((resp) => {
        outputVariableList.value = resp.data;
      })
      .catch((e) => {
        req.error = e;
        throw e;
      })
      .finally(() => {
        req.isFinished = true;
        req.isLoading = false;
      });
  }

  function getOperationList(projectId, bpId, nodeId) {
    getOperationListRequest.value = {};
    const req = getOperationListRequest.value;
    req.isLoading = true;
    return axios
      .get(api.autoformOperations({ projectId, bpId, nodeId }))
      .then((resp) => {
        operationList.value = resp.data;
      })
      .catch((e) => {
        req.error = e;
        throw e;
      })
      .finally(() => {
        req.isFinished = true;
        req.isLoading = false;
      });
  }

  function createAutoform(
    projectId,
    bpId,
    nodeId,
    name,
    instruction_steps,
    custom_variables,
    output_entities,
    auto_catalogs,
    map_layers,
  ) {
    createAutoformRequest.value = {};
    const req = createAutoformRequest.value;
    req.isLoading = true;

    return axios
      .post(api.autoforms({ projectId, bpId, nodeId }), {
        name,
        instruction_steps,
        custom_variables,
        output_entities,
        auto_catalogs,
        map_layers,
      })
      .then(() => {
        getAutoformList(projectId, bpId, nodeId);
      })
      .catch((e) => {
        req.error = e;
        throw e;
      })
      .finally(() => {
        req.isFinished = true;
        req.isLoading = false;
      });
  }

  function deleteAutoform(projectId, bpId, nodeId, autoformId) {
    deleteAutoformRequest.value = {};
    const req = deleteAutoformRequest.value;
    req.isLoading = true;

    return axios
      .delete(api.autoformById({ projectId, bpId, nodeId, autoformId }))
      .then(() => {
        getAutoformList(projectId, bpId, nodeId);
      })
      .catch((e) => {
        req.error = e;
        throw e;
      })
      .finally(() => {
        req.isFinished = true;
        req.isLoading = false;
      });
  }

  function updateAutoform(
    projectId,
    bpId,
    nodeId,
    autoformId,
    name,
    instruction_steps,
    custom_variables,
    output_entities,
    auto_catalogs,
    map_layers,
  ) {
    updateAutoformRequest.value = {};
    const req = updateAutoformRequest.value;
    req.isLoading = true;
    return axios
      .post(api.autoformById({ projectId, bpId, nodeId, autoformId }), {
        name,
        instruction_steps,
        custom_variables,
        output_entities,
        auto_catalogs,
        map_layers,
      })
      .then(() => {
        getAutoformList(projectId, bpId, nodeId);
      })
      .catch((e) => {
        req.error = e;
        throw e;
      })
      .finally(() => {
        req.isFinished = true;
        req.isLoading = false;
      });
  }

  function getAutoform(projectId, bpId, nodeId, autoformId) {
    getAutoformRequest.value = {};
    const req = getAutoformRequest.value;
    req.isLoading = true;
    return axios
      .get(api.autoformById({ projectId, bpId, nodeId, autoformId }))
      .then((resp) => {
        autoform.value = resp.data;
      })
      .catch((e) => {
        req.error = e;
        throw e;
      })
      .finally(() => {
        req.isFinished = true;
        req.isLoading = false;
      });
  }

  function generateUuid() {
    generateUuidRequest.value = {};
    const req = generateUuidRequest.value;
    req.isLoading = true;
    return axios
      .get(api.uuid())
      .then((resp) => {
        newUuid.value = resp.data;
        return resp.data;
      })
      .catch((e) => {
        req.error = e;
        throw e;
      })
      .finally(() => {
        req.isFinished = true;
        req.isLoading = false;
      });
  }

  function $reset() {
    autoformList.value = [];
    autoform.value = {};
    variableList.value = [];
    outputVariableList.value = [];
    operationList.value = [];
    selectedCustomVariableIndex.value = null;
    selectedInstructionStep.value = null;
    name.value = null;
    instruction_steps.value = [];
    custom_variables.value = [];
    output_entities.value = [];
    selectedOutputIndex.value = null;
    newUuid.value = null;
    autoCatalogs.value = [];
    selectedAutoCatalogIndex.value = null;
    mapLayers.value = [];
    selectedMapLayersIndex.value = null;

    getAutoformListRequest.value = {
      isLoading: false,
      isFinished: false,
      error: null
    };
    getVariableListRequest.value = {
      isLoading: false,
      isFinished: false,
      error: null
    };
    getOutputVariableListRequest.value = {
      isLoading: false,
      isFinished: false,
      error: null
    };
    getOperationListRequest.value = {
      isLoading: false,
      isFinished: false,
      error: null
    };
    createAutoformRequest.value = {
      isLoading: false,
      isFinished: false,
      error: null
    };
    deleteAutoformRequest.value = {
      isLoading: false,
      isFinished: false,
      error: null
    };
    updateAutoformRequest.value = {
      isLoading: false,
      isFinished: false,
      error: null
    };
    generateUuidRequest.value = {
      isLoading: false,
      isFinished: false,
      error: null
    };
    getAutoformRequest.value = {
      isLoading: false,
      isFinished: false,
      error: null
    };
  }

  return {
    getAutoformList,
    getOperationList,
    getVariableList,
    getOutputVariableList,
    createAutoform,
    updateAutoform,
    deleteAutoform,
    generateUuid,
    getAutoform,
    autoformList,
    operationList,
    variableList,
    variableTypeList,
    outputVariableList,
    selectedCustomVariableIndex,
    autoform,
    name,
    instruction_steps,
    custom_variables,
    output_entities,
    selectedInstructionStep,
    selectedOutputIndex,
    newUuid,
    getAutoformListRequest,
    getOperationListRequest,
    getVariableListRequest,
    getOutputVariableListRequest,
    createAutoformRequest,
    generateUuidRequest,
    updateAutoformRequest,
    getAutoformRequest,
    deleteAutoformRequest,
    autoCatalogs,
    selectedAutoCatalogIndex,
    mapLayers,
    selectedMapLayersIndex,

    $reset
  };
});
